import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import MarketingNav from "@/components/MarketingNav";
import Footer from "@/components/Footer";
import { toast } from "sonner";
import { Loader2, ArrowLeft, ExternalLink, Download, Star, Search, FileText } from "lucide-react";

function fmtSize(bytes) {
  if (!bytes) return "";
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(0)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

export default function AtividadesTurma() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [cls, setCls] = useState(null);
  const [acts, setActs] = useState([]);
  const [subjects, setSubjects] = useState({});
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [subjectFilter, setSubjectFilter] = useState("TODAS");

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/atividades/classes/${slug}/activities`),
      api.get(`/atividades/subjects`),
    ])
      .then(([r1, r2]) => {
        setCls(r1.data.class);
        setActs(r1.data.activities);
        const map = {};
        r2.data.forEach((s) => (map[s.name] = s.color));
        setSubjects(map);
        // count a view once per session per activity
        r1.data.activities.forEach((a) => {
          const key = `atv_view_${a.id}`;
          if (!sessionStorage.getItem(key)) {
            sessionStorage.setItem(key, "1");
            api.post(`/atividades/activities/${a.id}/view`).catch(() => {});
          }
        });
      })
      .catch(() => toast.error("Turma não encontrada."))
      .finally(() => setLoading(false));
  }, [slug]);

  const presentSubjects = useMemo(() => {
    const set = new Set(acts.map((a) => a.subject).filter(Boolean));
    return Array.from(set);
  }, [acts]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return acts.filter((a) => {
      if (subjectFilter !== "TODAS" && a.subject !== subjectFilter) return false;
      if (!q) return true;
      return (
        a.title.toLowerCase().includes(q) ||
        (a.description || "").toLowerCase().includes(q) ||
        (a.subject || "").toLowerCase().includes(q)
      );
    });
  }, [acts, query, subjectFilter]);

  const doAccess = async (a) => {
    try {
      const res = await api.post(`/atividades/activities/${a.id}/access`);
      const url = res.data.url;
      if (!url) return toast.error("Link não configurado.");
      if (url.startsWith("/")) navigate(url);
      else window.open(url, "_blank", "noopener");
    } catch {
      toast.error("Não foi possível acessar a atividade.");
    }
  };

  const doDownload = async (a) => {
    try {
      const res = await api.get(`/atividades/activities/${a.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = a.file_name || "arquivo";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Não foi possível baixar o arquivo.");
    }
  };

  const hasLink = (a) => ["external", "internal", "quiz", "link_file"].includes(a.activity_type) && (a.external_url || a.internal_url);

  return (
    <div className="min-h-screen bg-brand-bg flex flex-col">
      <MarketingNav />
      <main className="flex-1 max-w-6xl mx-auto w-full px-5 sm:px-8 py-10">
        <button onClick={() => navigate("/atividades")} className="inline-flex items-center gap-2 text-sm text-brand-muted hover:text-brand-ink transition-colors" data-testid="back-classes">
          <ArrowLeft className="w-4 h-4" /> Todas as turmas
        </button>

        <div className="mt-4 flex items-center gap-4">
          {cls && (
            <span className="w-14 h-14 rounded-2xl flex items-center justify-center font-heading font-black text-xl text-white shadow-lg" style={{ backgroundColor: cls.color || "#2563eb" }}>
              {cls.icon}
            </span>
          )}
          <h1 className="font-heading text-3xl sm:text-4xl font-black text-brand-ink">
            Atividades do {cls?.name || "..."}
          </h1>
        </div>

        <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:items-center">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-muted" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Pesquisar atividade..."
              data-testid="search-input"
              className="w-full pl-10 pr-4 h-11 rounded-full bg-white border border-slate-200 text-sm outline-none focus:border-brand-blue"
            />
          </div>
        </div>

        {presentSubjects.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2" data-testid="subject-filters">
            {["TODAS", ...presentSubjects].map((s) => (
              <button
                key={s}
                onClick={() => setSubjectFilter(s)}
                data-testid={`filter-${s}`}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                  subjectFilter === s ? "bg-brand-ink text-white" : "bg-white text-brand-muted border border-slate-200 hover:border-brand-blue"
                }`}
              >
                {s === "TODAS" ? "Todas" : s}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-brand-blue" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-24 text-brand-muted" data-testid="empty-state">
            <FileText className="w-10 h-10 mx-auto mb-3 opacity-40" />
            Nenhuma atividade disponível nesta turma ainda.
          </div>
        ) : (
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="activities-grid">
            {filtered.map((a) => {
              const color = subjects[a.subject] || "#64748b";
              return (
                <div key={a.id} data-testid={`activity-card-${a.id}`} className="group relative flex flex-col rounded-3xl bg-white border border-slate-100 overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300">
                  <div className="relative h-28 flex items-center justify-center overflow-hidden" style={{ backgroundColor: `${color}18` }}>
                    <span className="absolute inset-0 flex items-center justify-center font-heading font-black text-2xl" style={{ color }}>{a.subject}</span>
                    {a.thumbnail_url && (
                      <img src={`${API}/atividades/files/${a.thumbnail_url}`} alt="" className="relative w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                    )}
                    <div className="absolute top-3 left-3 flex gap-2 z-10">
                      {a.featured && (
                        <span className="inline-flex items-center gap-1 bg-brand-yellow text-brand-ink text-xs font-bold px-2 py-1 rounded-full shadow" data-testid="badge-featured">
                          <Star className="w-3 h-3 fill-brand-ink" /> Destaque
                        </span>
                      )}
                      {a.is_new && (
                        <span className="bg-brand-green text-white text-xs font-bold px-2 py-1 rounded-full shadow" data-testid="badge-new">NOVO</span>
                      )}
                    </div>
                  </div>
                  <div className="flex-1 flex flex-col p-5">
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color }}>
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} /> {a.subject}
                    </span>
                    <h3 className="mt-1.5 font-heading font-bold text-lg text-brand-ink leading-snug">{a.title}</h3>
                    {a.description && <p className="mt-1 text-sm text-brand-muted line-clamp-2">{a.description}</p>}
                    {a.activity_date && <p className="mt-2 text-xs text-brand-muted">{a.activity_date}</p>}
                    <div className="mt-4 pt-4 border-t border-slate-100 flex flex-wrap gap-2">
                      {hasLink(a) && (
                        <button onClick={() => doAccess(a)} data-testid={`access-${a.id}`} className="flex-1 min-w-[120px] inline-flex items-center justify-center gap-2 h-10 rounded-full bg-brand-blue text-white text-sm font-semibold hover:bg-blue-700 transition-colors">
                          <ExternalLink className="w-4 h-4" /> Acessar
                        </button>
                      )}
                      {a.has_file && (
                        <button onClick={() => doDownload(a)} data-testid={`download-${a.id}`} className="flex-1 min-w-[120px] inline-flex items-center justify-center gap-2 h-10 rounded-full bg-slate-100 text-brand-ink text-sm font-semibold hover:bg-slate-200 transition-colors">
                          <Download className="w-4 h-4" /> Baixar {a.file_type?.includes("pdf") ? "PDF" : ""}
                        </button>
                      )}
                    </div>
                    {a.has_file && a.file_size ? (
                      <p className="mt-2 text-[11px] text-brand-muted">{a.file_name} · {fmtSize(a.file_size)}</p>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
